import { createActorContext } from "@xstate/react"
import { and, assign, setup, enqueueActions } from "xstate"

import { navigateToPage } from "@/lib/flows/base/actions"
import type {
  BaseFlowContext,
  BaseFlowEvents,
  BaseFlowInput,
  BaseFlowMeta,
  FlowStep,
} from "@/lib/flows/base/types"
import { getExtraDataValidationFn } from "@/lib/integrations"

import type {
  CustomerApplications,
  CustomerFundingRequests,
} from "@boundless/bl-api-client/customer/models"

// Define types for application flow data
interface ApplicationFlowData {
  application?: CustomerApplications
  fundingNeeds?: CustomerFundingRequests
}

export const applicationFlowSteps: FlowStep[] = [
  {
    id: "start-applying",
    title: "Start Applying",
    states: [
      { stateId: "CreateFundingRequest", substep: 1 },
      { stateId: "ViewFundingRequest", substep: 1 },
    ],
  },
  {
    id: "lender-match",
    title: "Lender Match",
    states: [{ stateId: "LenderMatch", substep: 1 }],
  },
  {
    id: "application",
    title: "Application",
    states: [
      { stateId: "SignBoundlessApp", substep: 1 },
      { stateId: "UploadDocuments", substep: 2 },
    ],
  },
  {
    id: "submit",
    title: "Submit",
    states: [{ stateId: "ApplicationSummary", substep: 1 }],
  },
]

export const applicationFlowMachine = setup({
  types: {
    input: {} as BaseFlowInput & ApplicationFlowData,
    context: {} as BaseFlowContext & ApplicationFlowData,
    events: {} as
      | BaseFlowEvents
      | {
          type: "setApplication"
          application: CustomerApplications
        }
      | {
          type: "setFundingNeeds"
          fundingNeeds: CustomerFundingRequests
        },
    meta: {} as BaseFlowMeta,
  },
  actions: {
    navigateToPage,
    processNext: enqueueActions(({ context, enqueue }) => {
      if (context.onNextOverride) {
        try {
          context.onNextOverride()
        } catch (error) {
          console.error("Error executing onNextOverride callback:", error)
        }
        enqueue.assign(() => ({
          onNextOverride: undefined,
          onBackOverride: undefined,
        }))
      } else {
        enqueue.raise({ type: "proceedNext" })
      }
    }),
    processBack: enqueueActions(({ context, enqueue }) => {
      if (context.onBackOverride) {
        try {
          context.onBackOverride()
        } catch (error) {
          console.error("Error executing onBackOverride callback:", error)
        }
        enqueue.assign(() => ({
          onNextOverride: undefined,
          onBackOverride: undefined,
        }))
      } else {
        enqueue.raise({ type: "proceedBack" })
      }
    }),
  },
  guards: {
    hasLenderEasyApplyUrl: ({ context }) =>
      context.application?.easyApplyUrl === "",
    hasSignedBoundlessApp: ({ context }) =>
      context.fundingNeeds?.isSigned ?? false,
    hasAllRequiredDocuments: ({ context }) =>
      context.application?.hasAllRequiredDocuments ?? false,
    hasValidExtraData: ({ context: { application } }) => {
      if (!application) {
        return false
      }

      const validation = getExtraDataValidationFn(
        application.referralIntegration,
      )
      return validation(application.extraData)
    },
  },
}).createMachine({
  initial: "CreateFundingRequest",

  // Assign the initial context as the `input` value provided to the machine
  context: ({ input }) => {
    return input
  },

  // Global event handlers
  on: {
    next: { actions: "processNext" },
    back: { actions: "processBack" },
    setNextOverride: {
      actions: assign(({ event }) => ({
        onNextOverride: event.onNextOverride,
      })),
    },
    setBackOverride: {
      actions: assign(({ event }) => ({
        onBackOverride: event.onBackOverride,
      })),
    },
    setApplication: {
      actions: assign(({ event, context }) => {
        return {
          application: event.application,
          fundingNeeds: event.application.fundingNeeds ?? context.fundingNeeds,
        }
      }),
    },
    setFundingNeeds: {
      actions: assign(({ event }) => ({
        fundingNeeds: event.fundingNeeds,
      })),
    },
  },

  // State definitions
  states: {
    PreviousFlow: {
      entry: "navigateToPage",
      meta: {
        navigateOptions: {
          to: "/company/articles-of-incorporation",
        },
      },
    },
    CreateFundingRequest: {
      entry: "navigateToPage",
      meta: {
        navigateOptions: {
          to: "/funding-requests/create",
        },
      },
      on: {
        proceedNext: "LenderMatch",
        proceedBack: "PreviousFlow",
      },
    },
    ViewFundingRequest: {
      entry: "navigateToPage",
      meta: {
        navigateOptions: {
          to: "/funding-requests/$fundingNeedsUuid",
        },
      },
      on: {
        proceedNext: "LenderMatch",
        proceedBack: "PreviousFlow",
      },
    },
    LenderMatch: {
      entry: "navigateToPage",
      meta: {
        navigateOptions: {
          to: "/funding-requests/$fundingNeedsUuid/lender-match",
        },
      },
      on: {
        proceedNext: [
          {
            target: "ApplicationSummary",
            guard: and([
              "hasSignedBoundlessApp",
              "hasAllRequiredDocuments",
              "hasValidExtraData",
            ]),
          },
          { target: "UploadDocuments", guard: "hasSignedBoundlessApp" },
          { target: "SignBoundlessApp" },
        ],
        proceedBack: "ViewFundingRequest",
      },
    },
    SignBoundlessApp: {
      entry: "navigateToPage",
      meta: {
        navigateOptions: {
          to: "/applications/$applicationUuid/sign",
        },
      },
      on: {
        proceedNext: [
          {
            target: "ApplicationSummary",
            guard: and(["hasAllRequiredDocuments", "hasValidExtraData"]),
          },
          {
            target: "UploadDocuments",
          },
        ],
        proceedBack: "LenderMatch",
      },
    },
    UploadDocuments: {
      entry: "navigateToPage",
      meta: {
        navigateOptions: {
          to: "/applications/$applicationUuid/upload-documents",
        },
      },
      on: {
        proceedNext: {
          target: "ApplicationSummary",
          guard: and(["hasAllRequiredDocuments", "hasValidExtraData"]),
        },
        proceedBack: "SignBoundlessApp",
      },
    },
    ApplicationSummary: {
      entry: "navigateToPage",
      meta: {
        navigateOptions: {
          to: "/applications/$applicationUuid/submit",
        },
      },
      on: {
        proceedNext: "ApplicationFlowEnd",
        proceedBack: "UploadDocuments",
      },
    },
    ApplicationFlowEnd: {
      type: "final",
    },
  },
})
export const ApplicationFlowContext = createActorContext(applicationFlowMachine)
